import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/Tabs";
import { PageHeader } from "../components/layout/PageHeader";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import {
  MessageSquarePlus,
  Search,
  RefreshCw,
  Mail,
  FileText,
  FileCode,
} from "lucide-react";
import { useToast } from "../hooks/useToast";
import { EmailTemplateModal } from "../components/email/EmailTemplateModal";
import { EmailCampaignModal } from "../components/email/EmailCampaignModal";
import { Modal } from "../components/ui/Modal";
import * as conversationsService from "../services/conversationsService";

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  @media (min-width: ${({ theme }) => theme?.breakpoints?.md ?? 768}px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const mockDrafts = [
  { id: "d1", date: "2024-03-15", subject: "Monthly Newsletter Draft", lastEdited: "2024-03-15", status: "draft" },
  { id: "d2", date: "2024-03-14", subject: "Special Promotion Draft", lastEdited: "2024-03-14", status: "draft" },
];

const mockTemplates = [
  { id: "t1", name: "Welcome Email", description: "New customer welcome message", category: "Onboarding", lastUsed: "2024-03-10" },
  { id: "t2", name: "Order Confirmation", description: "Standard order confirmation template", category: "Transactional", lastUsed: "2024-03-12" },
];

const Conversations = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState("inbox");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [replyBody, setReplyBody] = useState("");
  const [replySending, setReplySending] = useState(false);
  const [newConvOpen, setNewConvOpen] = useState(false);
  const [newSubject, setNewSubject] = useState("");
  const [newContactName, setNewContactName] = useState("");
  const [newContactEmail, setNewContactEmail] = useState("");
  const [newMessage, setNewMessage] = useState("");
  const [creating, setCreating] = useState(false);
  const { toast } = useToast();

  const loadConversations = () => {
    setConversationsLoading(true);
    const params = statusFilter !== "all" ? { status: statusFilter } : {};
    conversationsService.fetchConversations(params).then((data) => {
      setConversations(Array.isArray(data) ? data : []);
    }).catch(() => setConversations([])).finally(() => setConversationsLoading(false));
  };

  useEffect(() => {
    loadConversations();
  }, [statusFilter]);

  const loadSelectedConversation = (id) => {
    if (!id) return;
    conversationsService.fetchConversationById(id).then((conv) => setSelectedConversation(conv)).catch(() => setSelectedConversation(null));
  };

  const filteredConversations = conversations.filter((c) => {
    const matchSearch =
      (c.subject || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (c.contactName || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (c.contactEmail || "").toLowerCase().includes(searchQuery.toLowerCase());
    return matchSearch;
  });

  const stats = [
    { labelKey: "conversations.total", value: conversations.length },
    { labelKey: "conversations.open", value: conversations.filter((c) => c.status === "open").length },
    { labelKey: "conversations.closed", value: conversations.filter((c) => c.status === "closed").length },
  ];

  const handleRefresh = () => {
    loadConversations();
    if (selectedConversation) loadSelectedConversation(selectedConversation._id);
    toast({ title: t("conversations.refreshing"), description: t("conversations.refreshingDesc") });
  };

  const handleAddReply = async (e) => {
    e.preventDefault();
    if (!selectedConversation?._id || !replyBody.trim()) return;
    setReplySending(true);
    try {
      const updated = await conversationsService.addMessage(selectedConversation._id, { body: replyBody.trim(), sender: "user" });
      setSelectedConversation(updated);
      setReplyBody("");
      toast({ title: t("conversations.replySent") });
    } catch (err) {
      toast({ title: t("conversations.failed"), description: err.message, variant: "destructive" });
    } finally {
      setReplySending(false);
    }
  };

  const handleCreateConversation = async (e) => {
    e.preventDefault();
    if (!newSubject.trim() || !newMessage.trim()) return;
    setCreating(true);
    try {
      const created = await conversationsService.createConversation({
        subject: newSubject.trim(),
        contactName: newContactName.trim() || undefined,
        contactEmail: newContactEmail.trim() || undefined,
        message: newMessage.trim(),
      });
      setConversations((prev) => [created, ...prev]);
      setNewConvOpen(false);
      setNewSubject("");
      setNewContactName("");
      setNewContactEmail("");
      setNewMessage("");
      setSelectedConversation(created);
      toast({ title: t("conversations.conversationCreated") });
    } catch (err) {
      toast({ title: t("conversations.failed"), description: err.message, variant: "destructive" });
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateStatus = async (convId, status) => {
    try {
      const updated = await conversationsService.updateConversationStatus(convId, status);
      setConversations((prev) => prev.map((c) => (c._id === convId ? updated : c)));
      if (selectedConversation?._id === convId) setSelectedConversation(updated);
      toast({ title: t("conversations.statusUpdated") });
    } catch (err) {
      toast({ title: t("conversations.failed"), description: err.message, variant: "destructive" });
    }
  };

  return (
    <div className="animate-fadeIn">
      <PageHeader
        title={t("conversations.title")}
        sub={t("conversations.subtitle")}
        actions={
          <>
            <Button $variant="outline" $size="sm" onClick={handleRefresh}>
              <RefreshCw size={14} />
              {t("conversations.refresh")}
            </Button>
            <Button $variant="outline" $size="sm" onClick={() => setTemplateModalOpen(true)}>
              <FileCode size={14} />
              {t("conversations.createTemplate")}
            </Button>
            <Button $variant="outline" $size="sm" onClick={() => setCampaignModalOpen(true)}>
              <Mail size={14} />
              {t("conversations.emailCampaign")}
            </Button>
            <Button $variant="outline" $size="sm" onClick={() => setNewConvOpen(true)}>
              <MessageSquarePlus size={14} />
              {t("conversations.newMessage")}
            </Button>
          </>
        }
      />

      {/* Conversation stats (marketing section) */}
      <StatsGrid style={{ marginBottom: 24 }}>
        {stats.map((s) => (
          <Card key={s.labelKey} style={{ padding: "16px", display: "flex", alignItems: "center", gap: "12px" }}>
            <div style={{ borderRadius: "9999px", padding: "12px", background: "rgba(26, 31, 44, 0.1)" }}>
              <Mail size={20} color="#1A1F2C" />
            </div>
            <div>
              <p style={{ margin: 0, fontSize: "12px", color: "#6b7280" }}>{t(s.labelKey)}</p>
              <p style={{ margin: "4px 0 0", fontSize: "22px", fontWeight: 600, color: "#111827" }}>{s.value}</p>
            </div>
          </Card>
        ))}
      </StatsGrid>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="inbox">{t("conversations.inbox")}</TabsTrigger>
          <TabsTrigger value="drafts">{t("conversations.drafts")}</TabsTrigger>
          <TabsTrigger value="templates">{t("conversations.templates")}</TabsTrigger>
        </TabsList>

        <TabsContent value="inbox">
          <div style={{ marginTop: 24, display: "flex", gap: 24, flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 320px", minWidth: 0, display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12 }}>
                <div style={{ position: "relative", flex: "1 1 280px", maxWidth: "400px" }}>
                  <Search style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", height: 16, width: 16, color: "#6b7280" }} />
                  <Input placeholder={t("conversations.searchConversations")} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} style={{ paddingLeft: 36 }} />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #d1d5db", minWidth: 160, fontSize: 14 }}
                >
                  <option value="all">{t("conversations.allMessages")}</option>
                  <option value="open">{t("conversations.open")}</option>
                  <option value="closed">{t("conversations.closed")}</option>
                </select>
              </div>
              <p style={{ margin: 0, fontSize: 13, color: "#6b7280" }}>
                {conversationsLoading ? t("conversations.loading") : t("conversations.showingCount", { count: filteredConversations.length, total: conversations.length })}
              </p>
              {!conversationsLoading && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {filteredConversations.map((conv) => (
                    <Card
                      key={conv._id}
                      style={{
                        padding: 16,
                        cursor: "pointer",
                        border: selectedConversation?._id === conv._id ? "2px solid var(--color-primary, #2563eb)" : undefined,
                      }}
                      onClick={() => { setSelectedConversation(conv); loadSelectedConversation(conv._id); }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                        <div>
                          <h4 style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 600 }}>{conv.subject || t("conversations.noSubject")}</h4>
                          <p style={{ margin: 0, fontSize: 12, color: "#6b7280" }}>
                            {conv.contactName || conv.contactEmail || "—"} {conv.contactEmail ? `<${conv.contactEmail}>` : ""}
                          </p>
                          {conv.messages?.length > 0 && (
                            <p style={{ margin: "4px 0 0", fontSize: 13, color: "#4b5563" }}>
                              {(conv.messages[conv.messages.length - 1].body || "").slice(0, 80)}{(conv.messages[conv.messages.length - 1].body || "").length > 80 ? "…" : ""}
                            </p>
                          )}
                        </div>
                        <span
                          style={{
                            padding: "4px 10px",
                            borderRadius: "9999px",
                            fontSize: 11,
                            fontWeight: 500,
                            background: conv.status === "open" ? "#dbeafe" : "#f3f4f6",
                            color: conv.status === "open" ? "#1e40af" : "#374151",
                            textTransform: "capitalize",
                          }}
                        >
                          {conv.status === "open" ? t("conversations.open") : t("conversations.closed")}
                        </span>
                      </div>
                    </Card>
                  ))}
                  {!conversationsLoading && filteredConversations.length === 0 && (
                    <p style={{ margin: 0, color: "#6b7280" }}>{t("conversations.noConversations")}</p>
                  )}
                </div>
              )}
            </div>
            {selectedConversation && (
              <Card style={{ flex: "1 1 360px", minWidth: 0, maxWidth: 560, display: "flex", flexDirection: "column", maxHeight: 480 }}>
                <div style={{ padding: 16, borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
                  <div>
                    <h3 style={{ margin: "0 0 4px", fontSize: 16 }}>{selectedConversation.subject || t("conversations.noSubject")}</h3>
                    <p style={{ margin: 0, fontSize: 12, color: "#6b7280" }}>{selectedConversation.contactName || ""} {selectedConversation.contactEmail ? `<${selectedConversation.contactEmail}>` : ""}</p>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <span style={{ padding: "4px 10px", borderRadius: 9999, fontSize: 12, background: selectedConversation.status === "open" ? "#dbeafe" : "#f3f4f6", color: selectedConversation.status === "open" ? "#1e40af" : "#374151" }}>
                      {selectedConversation.status === "open" ? t("conversations.open") : t("conversations.closed")}
                    </span>
                    <Button $variant="outline" $size="sm" onClick={() => handleUpdateStatus(selectedConversation._id, selectedConversation.status === "open" ? "closed" : "open")}>
                      {selectedConversation.status === "open" ? t("conversations.closeConversation") : t("conversations.reopenConversation")}
                    </Button>
                  </div>
                </div>
                <div style={{ flex: 1, overflow: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
                  {(selectedConversation.messages || []).map((msg, i) => (
                    <div
                      key={i}
                      style={{
                        alignSelf: msg.sender === "user" ? "flex-end" : "flex-start",
                        maxWidth: "85%",
                        padding: "10px 14px",
                        borderRadius: 12,
                        background: msg.sender === "user" ? "var(--color-primary, #2563eb)" : "#f3f4f6",
                        color: msg.sender === "user" ? "#fff" : "#111827",
                        fontSize: 14,
                      }}
                    >
                      {msg.body}
                    </div>
                  ))}
                </div>
                <form onSubmit={handleAddReply} style={{ padding: 16, borderTop: "1px solid #e5e7eb" }}>
                  <Input
                    placeholder={t("conversations.replyPlaceholder")}
                    value={replyBody}
                    onChange={(e) => setReplyBody(e.target.value)}
                    disabled={replySending}
                    style={{ marginBottom: 8 }}
                  />
                  <Button type="submit" $size="sm" disabled={replySending || !replyBody.trim()}>
                    {replySending ? t("conversations.sending") : t("conversations.sendReply")}
                  </Button>
                </form>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="drafts">
          <div style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 12 }}>
            {mockDrafts.map((draft) => (
              <Card key={draft.id} style={{ padding: 16, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ borderRadius: "9999px", padding: 8, background: "#f3f4f6" }}>
                    <FileText size={20} color="#6b7280" />
                  </div>
                  <div>
                    <h4 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{draft.subject}</h4>
                    <p style={{ margin: "4px 0 0", fontSize: 12, color: "#6b7280" }}>{t("conversations.lastEdited", { date: draft.lastEdited })}</p>
                  </div>
                </div>
                <Button $variant="outline" $size="sm">{t("conversations.editDraft")}</Button>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="templates">
          <div style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 12 }}>
            {mockTemplates.map((template) => (
              <Card key={template.id} style={{ padding: 16, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ borderRadius: "9999px", padding: 8, background: "#f3f4f6" }}>
                    <FileText size={20} color="#6b7280" />
                  </div>
                  <div>
                    <h4 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{template.name}</h4>
                    <p style={{ margin: "4px 0 0", fontSize: 12, color: "#6b7280" }}>{template.description}</p>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ padding: "4px 10px", borderRadius: 9999, fontSize: 12, background: "rgba(26, 31, 44, 0.08)", color: "#374151" }}>{template.category}</span>
                  <Button $variant="outline" $size="sm">{t("conversations.useTemplate")}</Button>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <EmailTemplateModal
        open={templateModalOpen}
        onClose={() => setTemplateModalOpen(false)}
        onSave={() => {}}
        toast={toast}
      />
      <EmailCampaignModal
        open={campaignModalOpen}
        onClose={() => setCampaignModalOpen(false)}
        onSubmit={() => {}}
        toast={toast}
      />
      <Modal open={newConvOpen} onClose={() => setNewConvOpen(false)} title={t("conversations.newConversation")}>
        <form onSubmit={handleCreateConversation} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <label style={{ display: "block", fontSize: 13, fontWeight: 500, marginBottom: 4 }}>{t("conversations.subject")} *</label>
            <Input value={newSubject} onChange={(e) => setNewSubject(e.target.value)} placeholder={t("conversations.subject")} required />
          </div>
          <div>
            <label style={{ display: "block", fontSize: 13, fontWeight: 500, marginBottom: 4 }}>{t("conversations.contactName")}</label>
            <Input value={newContactName} onChange={(e) => setNewContactName(e.target.value)} placeholder={t("conversations.contactName")} />
          </div>
          <div>
            <label style={{ display: "block", fontSize: 13, fontWeight: 500, marginBottom: 4 }}>{t("conversations.contactEmail")}</label>
            <Input type="email" value={newContactEmail} onChange={(e) => setNewContactEmail(e.target.value)} placeholder="email@example.com" />
          </div>
          <div>
            <label style={{ display: "block", fontSize: 13, fontWeight: 500, marginBottom: 4 }}>{t("conversations.firstMessage")} *</label>
            <textarea value={newMessage} onChange={(e) => setNewMessage(e.target.value)} placeholder={t("conversations.firstMessage")} required rows={4} style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14 }} />
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Button type="button" $variant="outline" onClick={() => setNewConvOpen(false)}>{t("common.cancel")}</Button>
            <Button type="submit" disabled={creating}>{creating ? t("conversations.sending") : t("conversations.createConversation")}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Conversations;
